import { type ReactNode, useContext } from 'react'

import type { ChartData, Point } from 'chart.js'

import { EmptyChartData } from '@/components/charts/empty-chart-data.tsx'
import { LineChart } from '@/components/charts/line-chart.tsx'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { EditChartButton } from '@/modules/sales-editor/components/edit-chart-button'
import { SalesEditableInput } from '@/modules/sales-editor/components/input'
import { ChartCustomizerContext } from '@/modules/sales-editor/state/chart-customizer'

interface TotalChartProps {
  title: ReactNode
  totalValue: string | number
  data?: ChartData<'line', (number | Point | null)[], unknown>
  variant?: 'default' | 'small' | 'big'
  isFetching: boolean
  isCurrency?: boolean
  hasLegend?: boolean
  showCents?: boolean
  formatTotalValue?: (value: string | number) => string
  testId?: string
}

export const TotalChart = ({
  title,
  totalValue,
  data,
  variant,
  isFetching,
  isCurrency,
  hasLegend,
  showCents,
  formatTotalValue,
  testId,
}: TotalChartProps) => {
  const editChartContext = useContext(ChartCustomizerContext)

  const total = (
    <SalesEditableInput
      value={totalValue}
      onChange={editChartContext?.onEditTotalValue}
      format={formatTotalValue}
      showInputIf={!!editChartContext}
    />
  )

  const editChartButton = !isFetching && editChartContext && (
    <EditChartButton onClick={editChartContext.onClickEditLineChart} />
  )

  if (variant === 'big') {
    return (
      <Card
        className="relative flex flex-col xl:justify-between"
        data-testid={testId}
      >
        <CardHeader className="pb-0">
          <CardTitle className="text-2xl font-semibold leading-none">
            {isFetching ? <Skeleton className="h-[24px] w-1/5" /> : total}
          </CardTitle>
          <CardDescription className="text-text-sub">
            <div className="grid grid-flow-row justify-between sm:grid-flow-col">
              <p data-testid={`${testId}-title`}>{!isFetching && title}</p>
              <div className="text-secondary-500 flex gap-3 text-sm font-medium dark:text-white">
                {hasLegend &&
                  data &&
                  data.datasets.map((item, i) => {
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-1"
                      >
                        <div
                          className="h-[8px] w-[8px] rounded"
                          style={{
                            backgroundColor: item.borderColor?.toString(),
                          }}
                        />
                        {item.label}
                      </div>
                    )
                  })}
              </div>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isFetching || !data ? (
            <EmptyChartData isFetching={isFetching} />
          ) : (
            <LineChart
              data={data}
              toggleOptions={{
                isTooltipCurrency: isCurrency,
                showLegendTitle: true,
                dateLabels: true,
              }}
              showCents={showCents}
            />
          )}
        </CardContent>
        {editChartButton}
      </Card>
    )
  }

  if (variant === 'small') {
    return isFetching ? (
      <Card
        className="p-6"
        data-testid={testId}
      >
        <Skeleton className="h-full w-full" />
      </Card>
    ) : (
      <Card
        className="relative grid grid-flow-col grid-cols-3"
        data-testid={testId}
      >
        <CardHeader className="col-span-2 h-full justify-center space-y-1 pr-0">
          <CardTitle className="text-text-default text-2xl font-semibold leading-none">
            {total}
          </CardTitle>
          <CardDescription
            className="text-text-sub"
            data-testid={`${testId}-title`}
          >
            {title}
          </CardDescription>
        </CardHeader>
        <CardContent className="col-span-1 flex items-center justify-center p-6 pl-0">
          {data && data.labels && data.labels.length > 0 ? (
            <LineChart
              data={data}
              toggleOptions={{
                hideLegend: true,
                isTooltipCurrency: isCurrency,
                dateLabels: true,
              }}
              options={{
                plugins: {
                  tooltip: {
                    yAlign: 'center',
                    titleFont: {
                      size: 12,
                    },
                    bodyFont: {
                      size: 10,
                    },
                  },
                },
              }}
            />
          ) : (
            <EmptyTotalChart
              title={title}
              isCurrency={isCurrency}
            />
          )}
        </CardContent>
        {editChartButton}
      </Card>
    )
  }

  return isFetching ? (
    <Card
      className="grid grid-flow-row grid-rows-3 gap-8"
      data-testid={testId}
    >
      <Skeleton className="row-span-2" />
      <Skeleton className="row-span-1" />
    </Card>
  ) : (
    <Card
      className="relative flex flex-col justify-between"
      data-testid={testId}
    >
      <CardHeader className="flex items-center justify-center">
        {data && data.labels && data.labels.length > 0 ? (
          <LineChart
            data={data}
            toggleOptions={{
              hideLegend: true,
              isTooltipCurrency: isCurrency,
              dateLabels: true,
            }}
          />
        ) : (
          <EmptyTotalChart
            title={title}
            isCurrency={isCurrency}
          />
        )}
      </CardHeader>
      <CardContent>
        <CardTitle className="text-text-default text-[32px] font-semibold leading-none">
          {total}
        </CardTitle>
        <CardDescription
          className="text-text-sub"
          data-testid={`${testId}-title`}
        >
          {title}
        </CardDescription>
      </CardContent>
      {editChartButton}
    </Card>
  )
}

interface EmptyTotalChartProps {
  title: ReactNode
  isCurrency?: boolean
}

const EmptyTotalChart = ({ title, isCurrency }: EmptyTotalChartProps) => {
  // it could be ReactNode, so we shouldn't render it, otherwise we will get an error
  const hasTitle =
    typeof title === 'string' ||
    typeof title === 'number' ||
    Array.isArray(title)

  return (
    <LineChart
      data={{
        labels: hasTitle ? [title, title] : ['', ''],
        datasets: [{ data: [0, 0], borderColor: '#0BC9CD' }],
      }}
      toggleOptions={{
        hideLegend: true,
        isTooltipCurrency: isCurrency,
        dateLabels: true,
        hideTooltip: true,
      }}
    />
  )
}
